import React, { useEffect, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, Plus, PaperPlaneTilt, FloppyDisk, CaretUp, CaretDown } from "@phosphor-icons/react";
import { FIELD_META, ROLE_LABELS, PARTY_COLORS, WITNESS_COLOR, SOLICITOR_COLOR } from "@/lib/esignApi";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DEFAULT_SIZES = {
  signature: { w: 0.24, h: 0.055 },
  name: { w: 0.26, h: 0.032 },
  address: { w: 0.30, h: 0.07 },
  date: { w: 0.16, h: 0.032 },
  deed_date: { w: 0.20, h: 0.032 },
};
const RENDER_WIDTH = 720;

const uid = () => Math.random().toString(36).slice(2, 10);

export default function FieldDesigner({ envelope, onClose, onSent }) {
  const isDeed = envelope.mode === "deed";
  const [parties, setParties] = useState(envelope.parties?.length ? envelope.parties : []);
  const [fields, setFields] = useState(envelope.fields || []);
  const [documents, setDocuments] = useState(envelope.documents || []);
  const [reordering, setReordering] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [selectedParty, setSelectedParty] = useState(envelope.parties?.[0]?.party_id || null);
  const [armed, setArmed] = useState(null); // {type, signer_kind}
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const pageRefs = useRef({});
  const drag = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await API.get(`/signing/envelopes/${envelope.id}/source.pdf`, { responseType: "blob" });
        setPdfUrl(URL.createObjectURL(resp.data));
      } catch (e) { toast.error("Could not load the document"); }
    })();
    (async () => { try { const { data } = await API.get(`/signing/templates`); setTemplates(data.templates || []); } catch { /* */ } })();
  }, [envelope.id]);

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    const newParties = (tpl.parties || []).map((p, i) => ({ party_id: uid(), role: p.role || "other", name: "", email: "", order: i }));
    const newFields = [];
    for (const f of (tpl.fields || [])) {
      if (numPages && f.page >= numPages) continue;
      const party_id = f.party_index == null ? null : (newParties[f.party_index]?.party_id || null);
      if (f.signer_kind !== "solicitor" && !party_id) continue;
      newFields.push({ field_id: uid(), party_id, signer_kind: f.signer_kind, type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, required: true });
    }
    setParties(newParties);
    setFields(newFields);
    setSelectedParty(newParties[0]?.party_id || null);
    toast.success(`Applied "${tpl.name}" — fill in the party names & emails`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this field layout (e.g. 'TR1 — sole buyer')");
    if (!name) return;
    const idx = {};
    parties.forEach((p, i) => { idx[p.party_id] = i; });
    try {
      await API.post(`/signing/templates`, {
        name, mode: envelope.mode,
        parties: parties.map((p, i) => ({ role: p.role, order: i })),
        fields: fields.map((f) => ({ party_index: f.party_id == null ? null : (idx[f.party_id] ?? null), signer_kind: f.signer_kind, type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h })),
      });
      const { data } = await API.get(`/signing/templates`);
      setTemplates(data.templates || []);
      toast.success("Layout saved as a reusable template");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const partyColor = useCallback((pid) => {
    const idx = parties.findIndex((p) => p.party_id === pid);
    return idx >= 0 ? PARTY_COLORS[idx % PARTY_COLORS.length] : SOLICITOR_COLOR;
  }, [parties]);

  const addParty = () => {
    const p = { party_id: uid(), role: "transferor", name: "", email: "", order: parties.length };
    setParties([...parties, p]);
    setSelectedParty(p.party_id);
  };
  const updateParty = (pid, patch) => setParties(parties.map((p) => (p.party_id === pid ? { ...p, ...patch } : p)));
  const removeParty = (pid) => {
    setParties(parties.filter((p) => p.party_id !== pid));
    setFields(fields.filter((f) => f.party_id !== pid));
    if (selectedParty === pid) setSelectedParty(null);
  };

  const placeField = (e, pageIdx) => {
    if (!armed) return;
    const rect = pageRefs.current[pageIdx].getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const sz = DEFAULT_SIZES[armed.type];
    const f = {
      field_id: uid(),
      party_id: armed.signer_kind === "solicitor" ? null : selectedParty,
      signer_kind: armed.signer_kind,
      type: armed.type,
      page: pageIdx,
      x: Math.max(0, Math.min(x, 1 - sz.w)),
      y: Math.max(0, Math.min(y, 1 - sz.h)),
      w: sz.w, h: sz.h, required: true,
    };
    setFields([...fields, f]);
    setArmed(null);
  };

  const onFieldMouseDown = (e, field, mode) => {
    e.stopPropagation();
    const rect = pageRefs.current[field.page].getBoundingClientRect();
    drag.current = {
      id: field.field_id, mode, pageW: rect.width, pageH: rect.height,
      startX: e.clientX, startY: e.clientY, orig: { ...field },
    };
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };
  const onDragMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = (e.clientX - d.startX) / d.pageW;
    const dy = (e.clientY - d.startY) / d.pageH;
    setFields((prev) => prev.map((f) => {
      if (f.field_id !== d.id) return f;
      if (d.mode === "move") {
        return { ...f, x: Math.max(0, Math.min(d.orig.x + dx, 1 - f.w)), y: Math.max(0, Math.min(d.orig.y + dy, 1 - f.h)) };
      }
      return { ...f, w: Math.max(0.05, Math.min(d.orig.w + dx, 1 - f.x)), h: Math.max(0.02, Math.min(d.orig.h + dy, 1 - f.y)) };
    }));
  };
  const onDragEnd = () => {
    drag.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  };

  const removeField = (fid) => setFields(fields.filter((f) => f.field_id !== fid));

  const canReorderDocs = documents.length > 1 && documents.every((d) => d.source_document_id);
  const reorderDocs = async (order) => {
    setReordering(true);
    try {
      // Persist current field geometry so the backend can remap pages correctly.
      await API.put(`/signing/envelopes/${envelope.id}/fields`, {
        fields: fields.map((f) => ({ field_id: f.field_id, party_id: f.party_id, signer_kind: f.signer_kind, type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, required: f.required })),
      });
      const { data } = await API.put(`/signing/envelopes/${envelope.id}/documents/reorder`, { order });
      setDocuments(data.documents || []);
      setFields((data.fields || []).map((f) => ({ ...f })));
      const resp = await API.get(`/signing/envelopes/${envelope.id}/source.pdf`, { responseType: "blob" });
      setPdfUrl(URL.createObjectURL(resp.data));
      toast.success("Document order updated — fields moved with their documents");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReordering(false); }
  };
  const moveDoc = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= documents.length) return;
    const order = documents.map((d) => d.source_document_id);
    [order[index], order[target]] = [order[target], order[index]];
    reorderDocs(order);
  };

  const persist = async () => {
    for (const p of parties) {
      if (!p.name.trim() || !p.email.trim()) { toast.error("Every party needs a name and email"); return false; }
    }
    await API.put(`/signing/envelopes/${envelope.id}/parties`, {
      parties: parties.map((p, i) => ({ party_id: p.party_id, role: p.role, name: p.name, email: p.email, order: i })),
    });
    await API.put(`/signing/envelopes/${envelope.id}/fields`, {
      fields: fields.map((f) => ({ field_id: f.field_id, party_id: f.party_id, signer_kind: f.signer_kind, type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, required: f.required })),
    });
    return true;
  };

  const save = async () => {
    setSaving(true);
    try { if (await persist()) toast.success("Draft saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const send = async () => {
    setSaving(true);
    try {
      if (!(await persist())) return;
      await API.post(`/signing/envelopes/${envelope.id}/send`);
      toast.success("Sent for signing — the first party has been emailed");
      onSent?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const paletteBtn = (type, signer_kind, color) => (
    <button
      key={`${signer_kind}-${type}`}
      onClick={() => setArmed(armed?.type === type && armed?.signer_kind === signer_kind ? null : { type, signer_kind })}
      disabled={signer_kind !== "solicitor" && !selectedParty}
      data-testid={`palette-${signer_kind}-${type}`}
      className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors mb-1 disabled:opacity-40 ${
        armed?.type === type && armed?.signer_kind === signer_kind ? "ring-2 ring-offset-1" : ""
      }`}
      style={{ borderColor: color.border, background: color.bg, color: color.text }}
    >
      + {FIELD_META[type].label}{signer_kind === "witness" ? " (witness)" : ""}
    </button>
  );

  const selColor = selectedParty ? partyColor(selectedParty) : PARTY_COLORS[0];

  return (
    <div className="flex flex-col h-full" data-testid="field-designer">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">{envelope.title}</h3>
          <p className="text-xs text-slate-500">{isDeed ? "Deed — witness required per signer" : "Standard document — no witness"}</p>
        </div>
        <div className="flex gap-2 items-center">
          {templates.length > 0 && (
            <Select onValueChange={(v) => applyTemplate(templates.find((t) => t.id === v))}>
              <SelectTrigger className="h-9 w-44 text-xs" data-testid="designer-template-select"><SelectValue placeholder="Apply layout…" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={saveTemplate} disabled={!fields.length} data-testid="designer-save-template">Save layout</Button>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="designer-close">Close</Button>
          <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="designer-save"><FloppyDisk className="w-4 h-4 mr-1" />Save draft</Button>
          <Button size="sm" onClick={send} disabled={saving || !parties.length} data-testid="designer-send"><PaperPlaneTilt className="w-4 h-4 mr-1" />Send for signing</Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left rail */}
        <div className="w-72 shrink-0 border-r border-slate-200 bg-slate-50 overflow-y-auto p-3">
          {canReorderDocs && (
            <div className="mb-4" data-testid="designer-doc-reorder">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Documents</span>
              <p className="text-[11px] text-slate-400 mb-2">Order they sign in. Fields move with their document.</p>
              <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                {documents.map((d, i) => (
                  <div key={d.source_document_id} className="flex items-center gap-2 px-2 py-1.5" data-testid={`designer-doc-${d.source_document_id}`}>
                    <span className="w-5 h-5 flex items-center justify-center rounded bg-slate-900 text-white text-[10px] font-semibold shrink-0">{i + 1}</span>
                    <span className="text-[11px] text-slate-700 truncate flex-1">{d.filename}</span>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button type="button" onClick={() => moveDoc(i, -1)} disabled={reordering || i === 0}
                        data-testid={`designer-doc-up-${d.source_document_id}`} aria-label="Move document up"
                        className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><CaretUp weight="bold" className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => moveDoc(i, 1)} disabled={reordering || i === documents.length - 1}
                        data-testid={`designer-doc-down-${d.source_document_id}`} aria-label="Move document down"
                        className="text-slate-400 hover:text-slate-900 disabled:opacity-30"><CaretDown weight="bold" className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Parties</span>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={addParty} data-testid="add-party"><Plus className="w-4 h-4" /></Button>
          </div>
          {parties.length === 0 && <p className="text-xs text-slate-400 mb-2">Add the transferor, transferee, borrower, lender, etc.</p>}
          {parties.map((p, i) => {
            const c = PARTY_COLORS[i % PARTY_COLORS.length];
            const sel = selectedParty === p.party_id;
            return (
              <div key={p.party_id} className={`mb-2 rounded-md border p-2 cursor-pointer ${sel ? "ring-2" : ""}`}
                style={{ borderColor: c.border, background: sel ? c.bg : "white" }}
                onClick={() => setSelectedParty(p.party_id)} data-testid={`party-row-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.border }} />
                  <button onClick={(e) => { e.stopPropagation(); removeParty(p.party_id); }} className="text-slate-400 hover:text-rose-600" data-testid={`party-remove-${i}`}><Trash className="w-3.5 h-3.5" /></button>
                </div>
                <Select value={p.role} onValueChange={(v) => updateParty(p.party_id, { role: v })}>
                  <SelectTrigger className="h-7 text-xs mb-1" data-testid={`party-role-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-7 text-xs mb-1" placeholder="Full name" value={p.name} onChange={(e) => updateParty(p.party_id, { name: e.target.value })} data-testid={`party-name-${i}`} />
                <Input className="h-7 text-xs" placeholder="Email" value={p.email} onChange={(e) => updateParty(p.party_id, { email: e.target.value })} data-testid={`party-email-${i}`} />
              </div>
            );
          })}

          <div className="mt-4 mb-2 text-xs font-semibold text-slate-700 uppercase tracking-wide">Add fields</div>
          <p className="text-[11px] text-slate-400 mb-2">{armed ? "Click on the document to place it." : selectedParty ? "Pick a field, then click the document." : "Select a party first."}</p>
          {paletteBtn("signature", "party", selColor)}
          {paletteBtn("name", "party", selColor)}
          {paletteBtn("address", "party", selColor)}
          {paletteBtn("date", "party", selColor)}
          {isDeed && <>
            <div className="mt-2 mb-1 text-[11px] font-semibold" style={{ color: WITNESS_COLOR.text }}>Witness (for selected party)</div>
            {paletteBtn("signature", "witness", WITNESS_COLOR)}
            {paletteBtn("name", "witness", WITNESS_COLOR)}
            {paletteBtn("address", "witness", WITNESS_COLOR)}
            <div className="mt-2 mb-1 text-[11px] font-semibold" style={{ color: SOLICITOR_COLOR.text }}>Solicitor</div>
            {paletteBtn("deed_date", "solicitor", SOLICITOR_COLOR)}
          </>}
        </div>

        {/* PDF canvas */}
        <div className="flex-1 overflow-auto bg-slate-200 p-6">
          {pdfUrl ? (
            <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div className="text-center text-slate-500 py-10">Loading document…</div>}>
              {Array.from({ length: numPages }, (_, idx) => (
                <div key={idx} className="mx-auto mb-6 shadow-lg bg-white relative"
                  style={{ width: RENDER_WIDTH, cursor: armed ? "crosshair" : "default" }}
                  ref={(el) => (pageRefs.current[idx] = el)}
                  onClick={(e) => placeField(e, idx)}
                  data-testid={`designer-page-${idx}`}>
                  <Page pageNumber={idx + 1} width={RENDER_WIDTH} renderTextLayer={false} renderAnnotationLayer={false} />
                  {fields.filter((f) => f.page === idx).map((f) => {
                    const c = f.signer_kind === "witness" ? WITNESS_COLOR : f.signer_kind === "solicitor" ? SOLICITOR_COLOR : partyColor(f.party_id);
                    return (
                      <div key={f.field_id}
                        onMouseDown={(e) => onFieldMouseDown(e, f, "move")}
                        data-testid={`field-box-${f.field_id}`}
                        className="absolute rounded-sm border-2 flex items-center justify-center text-[10px] font-semibold select-none group"
                        style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, borderColor: c.border, background: c.bg, color: c.text, cursor: "move" }}>
                        <span className="truncate px-1">{FIELD_META[f.type].label}{f.signer_kind === "witness" ? " (W)" : ""}</span>
                        <button onClick={(e) => { e.stopPropagation(); removeField(f.field_id); }}
                          className="absolute -top-2 -right-2 bg-white rounded-full border border-slate-300 w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100"
                          data-testid={`field-remove-${f.field_id}`}><Trash className="w-2.5 h-2.5 text-rose-600" /></button>
                        <div onMouseDown={(e) => onFieldMouseDown(e, f, "resize")}
                          className="absolute bottom-0 right-0 w-2.5 h-2.5 cursor-se-resize" style={{ background: c.border }} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </Document>
          ) : <div className="text-center text-slate-500 py-10">Loading document…</div>}
        </div>
      </div>
    </div>
  );
}
